/**
 * Компонент кнопок в хедере лендинга
 * 
 * Отображает кнопки "Войти" и "Оплатить" для неавторизованных пользователей.
 * Для авторизованных пользователей показывает UserAvatar и кнопку "Оплатить".
 * Применяет стилистику app-web с использованием styled-components.
 */

import React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { UserAvatar } from './UserAvatar';
import { RedButton, TransparentButton } from '@app/ui/pages/landing-page/button/button.styles';

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '12px',
  // Адаптивность для мобильных устройств
  [theme.breakpoints.down('sm')]: {
    gap: '8px',
  },
}));

export interface HeaderButtonsProps {
  /** Флаг авторизации пользователя */
  isAuthenticated: boolean;
  /** ФИО пользователя (для авторизованных) */
  userName?: string;
  /** Номер телефона пользователя (для авторизованных) */
  userPhone?: string;
  /** Callback при клике на кнопку "Войти" */
  onLoginClick: () => void;
  /** Callback при клике на кнопку "Оплатить" */
  onPaymentClick: () => void;
  /** Callback при клике на "Личный кабинет" в меню */
  onProfileClick: () => void;
  /** Callback при клике на "Выйти" в меню */
  onLogoutClick: () => void;
}

/**
 * Компонент кнопок в хедере лендинга
 * 
 * Отображает различный набор элементов в зависимости от статуса авторизации:
 * - Неавторизованный: кнопки "Войти" (outlined) и "Оплатить" (contained)
 * - Авторизованный: UserAvatar с выпадающим меню + кнопка "Оплатить"
 * 
 * Использует стилистику app-web с RedButton и TransparentButton.
 * 
 * @param isAuthenticated - Флаг авторизации пользователя
 * @param userName - ФИО пользователя (обязательно для авторизованных)
 * @param userPhone - Номер телефона пользователя (обязательно для авторизованных)
 * @param onLoginClick - Callback для открытия модального окна авторизации
 * @param onPaymentClick - Callback для открытия модального окна оплаты
 * @param onProfileClick - Callback для перехода в личный кабинет
 * @param onLogoutClick - Callback для выхода из учетной записи
 * 
 * @example
 * ```tsx
 * // Неавторизованный пользователь
 * <HeaderButtons
 *   isAuthenticated={false}
 *   onLoginClick={() => setLoginModalOpen(true)}
 *   onPaymentClick={() => setPaymentModalOpen(true)}
 *   onProfileClick={() => {}}
 *   onLogoutClick={() => {}}
 * />
 * 
 * // Авторизованный пользователь
 * <HeaderButtons
 *   isAuthenticated={true}
 *   userName="Иван Иванов"
 *   userPhone="+79001234567"
 *   onLoginClick={() => {}}
 *   onPaymentClick={() => setPaymentModalOpen(true)}
 *   onProfileClick={() => navigate('/lk')}
 *   onLogoutClick={() => handleLogout()}
 * />
 * ```
 */
export const HeaderButtons: React.FC<HeaderButtonsProps> = ({
  isAuthenticated,
  userName,
  userPhone,
  onLoginClick,
  onPaymentClick,
  onProfileClick,
  onLogoutClick,
}) => {
  return (
    <ButtonsContainer>
      {!isAuthenticated ? (
        <>
          {/* Кнопка "Войти" для неавторизованных */}
          <TransparentButton onClick={onLoginClick}>
            Войти
          </TransparentButton>
          
          {/* Кнопка "Оплатить" для неавторизованных */}
          <RedButton onClick={onPaymentClick}>
            Оплатить
          </RedButton>
        </>
      ) : (
        <>
          {/* Аватарка с выпадающим меню для авторизованных */}
          <UserAvatar
            userName={userName || ''}
            userPhone={userPhone || ''}
            onProfileClick={onProfileClick}
            onLogoutClick={onLogoutClick}
          />
          
          {/* Кнопка "Оплатить" для авторизованных */}
          <RedButton onClick={onPaymentClick}>
            Оплатить
          </RedButton>
        </>
      )}
    </ButtonsContainer>
  );
};
